import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";

import Footer from "./Footer";
import Header from "./Desktop/Header";
import TopContactBar from "./TopContactBar";
import SearchForm from "./Desktop/SearchForm";
import Tabs from "./Desktop/Tabs";
import RoomsSection from "./Desktop/RoomsSection";
import BookingSummary from "./Desktop/BookingSummary";
import AddOnsComponent from "./Desktop/AddOnsComponent";
import CheckoutComponent from "./CheckoutComponent";
import RoomImageSlider from "./Desktop/RoomImageSlider";
import CommentModal from "./Desktop/CommentModal";
import AuthModal from "./AuthModal";
import SignInRegisterModal from "./Desktop/SignInRegisterModal";
import { useNavigate, useSearchParams } from "react-router-dom";
import { fetchRoomAvailability, transformApiRoomsToLocalFormat } from "../services/availabilityApi";
import { auditAndReportCompleteness } from "../services/dataCompleteness";
import { getAddonList } from "../services/addonApi";
import { useAccommodationDetail } from "../hooks/useAccommodationDetail";
import { useAccommodationGroupDetail } from "../hooks/useAccommodationGroupDetail";
import { useHotelPriceJsonLd } from "../hooks/useHotelPriceJsonLd";
import { getURLParams, createDateAtMidnight, getAccommodationIdFromURL } from "../utils/urlParams";
import { initializeThemePromise } from "../utils/themeLoader";
import { isUserLoggedIn, getUserSession, getUserFullName, getAuthToken, getLoyaltyDiscount } from "../utils/auth";
import { logger } from "../utils/logger";
import { requestLoyaltyPointsForAccommodation } from "../services/loyaltyApi";
import { isTaxIncluded } from "../services/taxApi";
import { processAutoLogin, refreshLoyaltyDiscountForHotel } from "../services/loyaltyAutoLoginApi";
import { useTranslation } from "../context/TranslationContext";
import { captureTrackingParams, initializeAllTracking, isFromHotelAds, getSessionId, resetSessionId } from "../utils/trackingParams";
import { trackPageView, trackSearch, trackRoomSelected, trackAvailabilityResults } from "../services/analyticsService";
import useCustomScripts from "../hooks/useCustomScripts";

// Removed hardcoded addOns - using only API data

const DesktopComponent = () => {
  const { t, changeLanguage } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Promo code — controlled so the typed value actually reaches the API
  const [promoCode, setPromoCode] = useState(searchParams.get('promo') || '');

  // Increment to externally open the date picker (e.g. "Change Dates" no-rooms button)
  const [openCalendarSignal, setOpenCalendarSignal] = useState(0);

  // Capture tracking params (utm_campaign=HotelAds, _ga) on page load & generate new sessionId
  useEffect(() => {
    captureTrackingParams(searchParams);
    // Always generate a fresh session ID on first page
    const sessionId = resetSessionId();
    // Analytics: track page view
    // Build from the LIVE URL, not the (possibly stale) searchParams hook, so a
    // domain-resolved hotel_id injected by HomeRoute is not stripped here.
    const liveParams = new URLSearchParams(window.location.search);
    const hotelId = liveParams.get('hotel_id') || liveParams.get('accommodationid') || '';
    trackPageView(hotelId, { page: 'availability' });
    liveParams.set('sessionId', sessionId);
    setSearchParams(liveParams, { replace: true });
  }, []);

  // Set default dates to today and tomorrow
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);

  // Helper to format date as YYYY-MM-DD (for storage/API)
  const formatDate = (date) => {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return '';
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper to format date as DD-MM-YYYY for API
  const formatDateForAPI = (dateString) => {
    const date = dateString instanceof Date ? dateString : new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Read URL parameters using shared utility
  const urlParams = getURLParams(searchParams);

  const [showSignInModal, setShowSignInModal] = useState(false);
  const [signInTab, setSignInTab] = useState('signin');

  // Login status state
  const [isLoggedIn, setIsLoggedIn] = useState(() => isUserLoggedIn());
  
  // Check login status when component mounts and when modal closes
  useEffect(() => {
    const checkLoginStatus = () => {
      const loggedIn = isUserLoggedIn();
      setIsLoggedIn(loggedIn);

      // Log full user details if logged in
      if (loggedIn) {
        const session = getUserSession();
        console.log('====== 👤 LOGGED-IN USER DETAILS ======');
        console.log('Full Name:', getUserFullName());
        console.log('Auth Token:', getAuthToken());
        console.log('Session:', session);
        console.log('Profile:', session?.user?.profile);
        console.log('Profile Data:', session?.user?.data);
        console.log('Login Time:', session?.loginTime);
        console.log('Loyalty Discount:', getLoyaltyDiscount());
        console.log('======================================');
      }
    };

    checkLoginStatus();

    // Check login status when modal closes
    if (!showSignInModal) {
      checkLoginStatus();
    }
  }, [showSignInModal]);

  // Listen for login/logout events to update state automatically
  useEffect(() => {
    const handleLoginStatusChange = () => {
      const loggedIn = isUserLoggedIn();
      setIsLoggedIn(loggedIn);
      // Hide banner and store group_id when user logs in
      if (loggedIn) {
        setShowLoyaltyBanner(false);
        // Store current group_id so we can keep login across hotels in same group
        const currentGroupId = accommodationGroupDetail?.groupId || accommodationGroupDetail?.hotelMapping?.GroupId || urlParams.group_id;
        if (currentGroupId) {
          localStorage.setItem('loggedInGroupId', String(currentGroupId));
        }
      }
    };

    // Listen for both login and logout events
    window.addEventListener('userLoggedIn', handleLoginStatusChange);
    window.addEventListener('userLoggedOut', handleLoginStatusChange);

    return () => {
      window.removeEventListener('userLoggedIn', handleLoginStatusChange);
      window.removeEventListener('userLoggedOut', handleLoginStatusChange);
    };
  }, []);
  
  
  // Helper to get currency symbol from code
  const getCurrencySymbol = (code) => {
    const codeStr = code ? String(code) : '';
    const upperCode = codeStr.toUpperCase();
    const symbols = {
      'INR': '₹', 'USD': '$', 'EUR': '€', 'GBP': '£', 'AED': 'AED',
      'SAR': 'SAR', 'QAR': 'QAR', 'KWD': 'KWD', 'OMR': 'OMR', 'BHD': 'BHD',
      'PKR': 'Rs'
    };
    return symbols[upperCode] || upperCode;
  };

  // Helper to get currency name from code
  const getCurrencyName = (code) => {
    const codeStr = code ? String(code) : '';
    const upperCode = codeStr.toUpperCase();
    const names = {
      'INR': 'Indian Rupee', 'USD': 'US Dollar', 'EUR': 'Euro', 'GBP': 'British Pound',
      'AED': 'UAE Dirham', 'SAR': 'Saudi Riyal', 'QAR': 'Qatari Riyal',
      'KWD': 'Kuwaiti Dinar', 'OMR': 'Omani Rial', 'BHD': 'Bahraini Dinar'
    };
    return names[upperCode] || upperCode;
  };

  // Currency state - prioritize URL parameter, then localStorage, no default
  const [currentCurrency, setCurrentCurrency] = useState(() => {
    // First check URL parameter
    const urlCurrency = urlParams.currency?.toUpperCase();
    if (urlCurrency) {
      return {
        code: urlCurrency,
        symbol: getCurrencySymbol(urlCurrency),
        rate: 1,
        name: getCurrencyName(urlCurrency),
      };
    }

    // Then check localStorage
    const saved = localStorage.getItem('selectedCurrency');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        // If parse fails, return null
      }
    }

    // No default - return null
    return null;
  });

  // Language state - load from localStorage
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    const saved = localStorage.getItem('selectedLanguage');
    if (saved) {
      try {
        const lang = JSON.parse(saved);
        // Apply RTL if needed - minimal changes
        const rtlLanguages = ['AR', 'HE', 'FA', 'UR'];
        const isRTL = rtlLanguages.includes(lang.code);
        if (isRTL) {
          document.documentElement.setAttribute('dir', 'rtl');
          document.documentElement.setAttribute('lang', 'ar');
        } else {
          document.documentElement.setAttribute('dir', 'ltr');
          document.documentElement.setAttribute('lang', 'en');
        }
        return lang;
      } catch (e) {
        // If parse fails, use default
      }
    }
    return {
      code: "EN",
      flag: "https://flagcdn.com/w80/us.png",
      flagEmoji: "🇺🇸",
      name: "English",
      nativeName: "English",
    };
  });
  // Core state variables
  const [selectedDates, setSelectedDates] = useState(() => {
    const checkInDate = urlParams.checkin 
      ? createDateAtMidnight(urlParams.checkin) 
      : (() => {
          const d = new Date();
          d.setHours(0, 0, 0, 0);
          return d;
        })();
    const checkOutDate = urlParams.checkout 
      ? createDateAtMidnight(urlParams.checkout) 
      : (() => {
          const d = new Date(checkInDate);
          d.setDate(d.getDate() + 1);
          return d;
        })();
    return {
      checkIn: checkInDate,
      checkOut: checkOutDate,
    };
  });
  const [guests, setGuests] = useState({ 
    adults: urlParams.adult || 1, 
    children: urlParams.child || 0 
  });
  // Child ages state - array of ages for each child
  const [childAges, setChildAges] = useState(() => {
    // Initialize from URL or create empty array based on number of children
    const initialAges = Array.isArray(urlParams.childAges) ? urlParams.childAges : [];
    const numChildren = urlParams.child || 0;
    // If URL has ages, use them; otherwise create array with default age 1
    if (initialAges.length === numChildren) {
      return initialAges;
    }
    // Create array with default age 1 for each child
    return Array(numChildren).fill(1);
  });
  const [accommodationId, setAccommodationId] = useState(() => {
    return getAccommodationIdFromURL(searchParams, '');
  });

  // Loyalty banner state (dismissible) - per hotel basis (defined after accommodationId)
  const [showLoyaltyBanner, setShowLoyaltyBanner] = useState(() => {
    // Only show if user is not logged in and banner hasn't been dismissed for this hotel
    const hotelId = urlParams.hotel_id || urlParams.accommodationid || accommodationId || '2010';
    const dismissedKey = `loyaltyBannerDismissed_${hotelId}`;
    const dismissed = localStorage.getItem(dismissedKey) === 'true';
    const userLoggedIn = isUserLoggedIn();
    const shouldShow = !userLoggedIn && !dismissed;
    return shouldShow;
  });

  // Member discount percentage from requestLoyaltyPointsForAccommodation API
  const [memberDiscountPercent, setMemberDiscountPercent] = useState(0);
  // Bumped after the per-hotel loyalty discount cache is refreshed, to force
  // rate cards to re-read the fresh (per-hotel) discount.
  const [loyaltyTick, setLoyaltyTick] = useState(0);

  // Theme loading state - show fade loader until theme CSS is loaded
  const [isThemeLoaded, setIsThemeLoaded] = useState(false);

  // Update banner visibility when login status changes or hotel changes
  useEffect(() => {
    const hotelId = urlParams.hotel_id || urlParams.accommodationid || accommodationId || '2010';
    const dismissedKey = `loyaltyBannerDismissed_${hotelId}`;
    
    if (isLoggedIn) {
      setShowLoyaltyBanner(false);
    } else {
      // Only show if not dismissed for this specific hotel
      const dismissed = localStorage.getItem(dismissedKey) === 'true';
      const shouldShow = !dismissed;
      setShowLoyaltyBanner(shouldShow);
    }
  }, [isLoggedIn, accommodationId, searchParams]);
  
  // Request loyalty points for accommodation on page load
  useEffect(() => {
    const fetchLoyaltyPoints = async () => {
      try {
        // Get accommodationId and hotelId from URL
        const accId = urlParams.accommodationid || accommodationId || searchParams.get('accommodationid') || searchParams.get('hotel_id');
        const hotelId = urlParams.hotel_id || urlParams.accommodationid || accommodationId || searchParams.get('hotel_id') || searchParams.get('accommodationid');

        if (accId && hotelId) {
          const response = await requestLoyaltyPointsForAccommodation({
            accommodationId: accId,
            hotelId: hotelId
          });

          // Extract member discount percentage from redeem_override.Value
          // API response structure: response.data.data.redeem_override.Value
          const redeemOverride = response?.data?.data?.redeem_override || response?.data?.redeem_override || response?.redeem_override;
          const discountValue = redeemOverride ? parseFloat(redeemOverride.Value || redeemOverride.value || 0) : 0;
          // Always set (reset to 0 for hotels with no member discount) so a
          // previous hotel's percentage doesn't stick when switching hotels.
          setMemberDiscountPercent(discountValue > 0 ? discountValue : 0);

          // Refresh the logged-in member's redeem-override cache for THIS hotel
          // so one hotel's member discount doesn't leak onto another, then force
          // the rate cards to re-read the fresh value.
          await refreshLoyaltyDiscountForHotel(accId);
          setLoyaltyTick(t => t + 1);
        } else {
        }
      } catch (error) {
        // Don't block page load if this API call fails
      }
    };

    fetchLoyaltyPoints();
  }, [accommodationId, searchParams, urlParams.accommodationid, urlParams.hotel_id]);

  // Handle banner dismiss - per hotel basis
  // Loyalty banner is not user-dismissable (no close button) — always shown to
  // signed-out members until they sign in.

  // Check if hotel_id is available in URL (check actual URL params, not default value)
  const hasHotelId = searchParams.get('hotel_id') || searchParams.get('accommodationid');
  
  // Handle accommodation change from dropdown
  const handleAccommodationChange = (newAccommodationId) => {
    setAccommodationId(newAccommodationId.toString());
    
    // Update URL with new accommodation ID
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('hotel_id', newAccommodationId.toString());
    setSearchParams(newSearchParams, { replace: true });
  };
  const [selectedRooms, setSelectedRooms] = useState([]);
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [activeTab, setActiveTab] = useState("rooms");
  const [showCheckout, setShowCheckout] = useState(false);

  // API integration state
  const [apiRooms, setApiRooms] = useState([]);
  const [allApiRooms, setAllApiRooms] = useState([]); // Store all rooms from API
  const [displayedRoomsCount, setDisplayedRoomsCount] = useState(5); // Initial display count
  const [isLoadingRooms, setIsLoadingRooms] = useState(true); // Start true to keep full-page loader until first search completes
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [roomsError, setRoomsError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  // Landing mode: no checkin/checkout in URL → show search form centered, no auto-search.
  const [isLandingMode, setIsLandingMode] = useState(false);

  // AddOns state
  const [addOns, setAddOns] = useState([]);
  const [isLoadingAddOns, setIsLoadingAddOns] = useState(false);
  const [addOnsError, setAddOnsError] = useState(null);
  
  // Use shared hook for accommodation detail (used in both mobile and desktop)
  const { accommodationDetail, isLoadingAccommodation } = useAccommodationDetail(accommodationId);

  // Google Hotel-Price JSON-LD (SEO only). READ-ONLY + crash-safe + purely additive:
  // it only reads hotel/rooms data and writes a <head> script — it never touches any
  // pricing calculation or the reservation flow. Every field is guarded; if a value is
  // missing/empty the builder returns null and nothing is injected (no break, no crash).
  useHotelPriceJsonLd({
    name: accommodationDetail?.accommodationName || accommodationDetail?.AccommodationName,
    identifier: accommodationId,
    address: accommodationDetail?.Address || accommodationDetail?.address,
    country: accommodationDetail?.CountryName,
    checkinDate: formatDate(selectedDates?.checkIn),
    checkoutDate: formatDate(selectedDates?.checkOut),
    rooms: apiRooms,
  });

  // Use shared hook for accommodation group detail
  const { accommodationGroupDetail, isLoadingGroup } = useAccommodationGroupDetail(accommodationId);
  useCustomScripts(accommodationGroupDetail);

  // Guest selector gating (ALL hotels). Precedence: check IsAdultChild FIRST —
  // if 1, show BOTH adults & children. Otherwise fall back to the individual
  // IsAdult / IsChild flags. If none of the 3 flags exist in the data (legacy
  // hotels), show both. Never hide everything (a booking needs adults).
  const _adFlags = accommodationGroupDetail?.accommodationdetailsdb?.[0] || {};
  const _isAdultFlag = Number(_adFlags.IsAdult ?? accommodationDetail?.IsAdult ?? 0);
  const _isChildFlag = Number(_adFlags.IsChild ?? accommodationDetail?.IsChild ?? 0);
  const _isAdultChildFlag = Number(_adFlags.IsAdultChild ?? accommodationDetail?.IsAdultChild ?? 0);
  const _guestFlagsPresent = _adFlags.IsAdultChild != null || _adFlags.IsAdult != null || _adFlags.IsChild != null
    || accommodationDetail?.IsAdultChild != null || accommodationDetail?.IsAdult != null || accommodationDetail?.IsChild != null;
  const showChildrenOption = !_guestFlagsPresent ? true : (_isAdultChildFlag === 1 || _isChildFlag === 1);
  const _showAdultsRaw = !_guestFlagsPresent || _isAdultChildFlag === 1 || _isAdultFlag === 1;
  const showAdultsOption = _showAdultsRaw || !showChildrenOption; // never hide both

  // Auto-login via ltkn URL param (external loyalty token)
  // Triggers once accommodationGroupDetail loads (needs PartnerKey)
  useEffect(() => {
    const ltkn = searchParams.get('ltkn');
    if (!ltkn || !accommodationGroupDetail) return;

    let cancelled = false;

    (async () => {
      const result = await processAutoLogin(searchParams, accommodationGroupDetail);
      if (cancelled) return;

      if (result?.status === true && result?.profile) {
        setIsLoggedIn(true);
        setShowLoyaltyBanner(false);
      }
    })();

    return () => { cancelled = true; };
  }, [searchParams, accommodationGroupDetail]);

  // Update document title dynamically based on accommodation name
  // Only set title when accommodation detail is loaded (not during loading)
  useEffect(() => {
    let hotelName = '';
    if (accommodationDetail && (accommodationDetail.AccommodationName || accommodationDetail.accommodationName)) {
      hotelName = accommodationDetail.AccommodationName || accommodationDetail.accommodationName;
    } else if (accommodationGroupDetail) {
      const firstAccommodation = accommodationGroupDetail.accommodationDetails?.[0] || accommodationGroupDetail.accommodationdetailsdb?.[0];
      if (firstAccommodation && firstAccommodation.AccommodationName) {
        hotelName = firstAccommodation.AccommodationName;
      } else if (accommodationGroupDetail.groupName) {
        hotelName = accommodationGroupDetail.groupName;
      }
    }

    document.title = hotelName || 'Loading...';

    // Set canonical URL
    document.querySelector('link[rel="canonical"]').href = window.location.href;

    if (hotelName) {
      const hotelImage = accommodationDetail?.LogoURL || accommodationDetail?.logoURL || accommodationDetail?.LogoUrl || '';
      const desc = `Book your stay at ${hotelName}. Best rates guaranteed.`;

      // Meta description
      document.querySelector('meta[name="description"]').content = desc;

      // Open Graph tags
      document.querySelector('meta[property="og:title"]').content = hotelName;
      document.querySelector('meta[property="og:description"]').content = desc;
      document.querySelector('meta[property="og:url"]').content = window.location.href;
      document.querySelector('meta[property="og:image"]').content = hotelImage;
    }
  }, [accommodationDetail, accommodationGroupDetail]);

  // Update favicon dynamically based on hotel logo
  useEffect(() => {
    let faviconLink = document.querySelector("link[rel*='icon']") || document.createElement('link');
    
    if (!document.querySelector("link[rel*='icon']")) {
      faviconLink.type = 'image/x-icon';
      faviconLink.rel = 'shortcut icon';
      document.getElementsByTagName('head')[0].appendChild(faviconLink);
    }
    
    if (accommodationDetail && (accommodationDetail.LogoURL || accommodationDetail.logoURL || accommodationDetail.LogoUrl)) {
      faviconLink.href = accommodationDetail.LogoURL || accommodationDetail.logoURL || accommodationDetail.LogoUrl;
    } else if (accommodationGroupDetail) {
      // Try to get logo from first accommodation in group
      const firstAccommodation = accommodationGroupDetail.accommodationDetails?.[0] || accommodationGroupDetail.accommodationdetailsdb?.[0];
      if (firstAccommodation && (firstAccommodation.LogoURL || firstAccommodation.LogoUrl || firstAccommodation.logoURL)) {
        faviconLink.href = firstAccommodation.LogoURL || firstAccommodation.LogoUrl || firstAccommodation.logoURL;
      }
    }
  }, [accommodationDetail, accommodationGroupDetail]);
  
  // Get group_id from accommodationGroupDetail or URL
  const groupId = accommodationGroupDetail?.groupId || accommodationGroupDetail?.hotelMapping?.GroupId || urlParams.group_id || null;
  const isAccommodationDropDown = accommodationGroupDetail?.hotelMapping?.IsAccommodationDropDown
    ?? accommodationGroupDetail?.accommodationdetailsdb?.[0]?.IsAccommodationDropDown
    ?? accommodationGroupDetail?.IsAccommodationDropDown
    ?? 1;
  const hasLoyalty = accommodationGroupDetail?.hotelMapping?.HasLoyalty
    ?? accommodationGroupDetail?.hotelMapping?.HasLayalty
    ?? accommodationGroupDetail?.accommodationdetailsdb?.[0]?.HasLoyalty
    ?? accommodationGroupDetail?.accommodationdetailsdb?.[0]?.HasLayalty
    ?? 0;

  // Hotel change loyalty logic:
  // - Same group → keep login (continue session across hotels in same group,
  //   even if a particular hotel has loyalty disabled — the member logged in to
  //   the GROUP, so a sister hotel shouldn't log them out; they just see no
  //   member discount there)
  // - Different group → logout and clear all loyalty data
  // Uses localStorage 'loggedInGroupId' to persist across page reloads
  useEffect(() => {
    if (!accommodationGroupDetail) return; // Wait for group data to load

    const hasLoyaltyEnabled = Number(hasLoyalty) === 1;
    const storedGroupId = localStorage.getItem('loggedInGroupId');
    // Treat group id 0 / "0" as NO group — these are standalone single hotels,
    // not a real chain. Switching between such hotels must log the user out, so
    // never count them as "same group".
    const currentGroupId = groupId && String(groupId) !== '0' ? String(groupId) : null;
    const sameGroup = storedGroupId && currentGroupId && storedGroupId === currentGroupId;

    // Same group → keep login regardless of this hotel's loyalty flag
    if (sameGroup) {
      return;
    }

    // First-time stamp ONLY: record the group for a logged-in member who has no
    // stored group yet (e.g. auto-login via ltkn, which bypasses the
    // userLoggedIn handler that normally stamps it). Must NOT overwrite an
    // existing group — otherwise moving to a DIFFERENT group would silently
    // re-stamp here and skip the logout below.
    if (hasLoyaltyEnabled && isLoggedIn && currentGroupId && !storedGroupId) {
      localStorage.setItem('loggedInGroupId', currentGroupId);
      return;
    }

    // Otherwise: different group or no loyalty → clear all loyalty data
    const hasLoyaltyData = localStorage.getItem('authToken') ||
                           localStorage.getItem('loyaltyDiscount') ||
                           localStorage.getItem('availableLoyaltyPoints');

    if (hasLoyaltyData) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('userSession');
      localStorage.removeItem('loyaltyDiscount');
      localStorage.removeItem('availableLoyaltyPoints');
      localStorage.removeItem('loggedInGroupId');
      setIsLoggedIn(false);
      setMemberDiscountPercent(0);
      // Notify other components about logout
      window.dispatchEvent(new Event('userLoggedOut'));
    }
  }, [accommodationId, accommodationGroupDetail, hasLoyalty, groupId]);

  // Load theme CSS - Priority: Group CSS > Hotel CSS > Default
  // Always pass all available data to initializeThemePromise and let it handle priority internally
  // Uses promise-based loading with fade loader
  useEffect(() => {
    const loadTheme = async () => {
      setIsThemeLoaded(false); // Show loader

      try {
        // Always call with all available data - themeLoader handles priority internally:
        // 1. First checks accommodationGroupDetail for groupId → loads Group CSS
        // 2. If no groupId, falls back to hotelId → loads Hotel CSS
        // 3. If neither, loads default modern-mobile-ui.css
        const themeResult = await initializeThemePromise(searchParams, accommodationDetail, accommodationGroupDetail);

      } catch (error) {
        // Theme loading error - silently handled
      } finally {
        setIsThemeLoaded(true); // Hide loader
      }
    };

    loadTheme();
  }, [searchParams, accommodationDetail, accommodationGroupDetail]);

  // Initialize Microsoft Clarity + GTM tracking when accommodation data is loaded
  useEffect(() => {
    if (accommodationDetail) {
      initializeAllTracking({
        accommodationName: accommodationDetail.AccommodationName || accommodationDetail.accommodationName,
        accommodationId: accommodationDetail.AccommodationId || accommodationDetail.accommodationId || urlParams.hotel_id,
        checkIn: urlParams.checkin,
        checkOut: urlParams.checkout,
        adults: parseInt(urlParams.adult) || 1,
        children: parseInt(urlParams.child) || 0,
        isAvailable: apiRooms && apiRooms.length > 0, // Available if rooms exist
        accommodationGroupDetail: accommodationGroupDetail // For GTM container ID (GtmCode)
      });
    }
  }, [accommodationDetail, accommodationGroupDetail, apiRooms, urlParams]);

  // Sync currency from URL parameter - URL currency takes priority over API currency
  // If URL has currency, use it and don't let API override
  const [hasApiCurrency, setHasApiCurrency] = useState(() => {
    // If URL already has currency, mark as true to prevent API override
    return !!urlParams.currency;
  });

  useEffect(() => {
    // If URL has currency, always use it (priority over API)
    const urlCurrency = urlParams.currency?.toUpperCase();
    if (urlCurrency && (!currentCurrency || currentCurrency.code !== urlCurrency)) {
      const currencyFromUrl = {
        code: urlCurrency,
        symbol: getCurrencySymbol(urlCurrency),
        rate: 1,
        name: getCurrencyName(urlCurrency),
      };
      setCurrentCurrency(currencyFromUrl);
      setHasApiCurrency(true); // Prevent API from overriding URL currency
      localStorage.setItem('selectedCurrency', JSON.stringify(currencyFromUrl));
    }
  }, [urlParams.currency]);

  // Set currency from accommodationDetail when it's loaded
  // Priority: Availability API currency > Accommodation API currency
  // This ensures currency is always set from accommodationDetail API, especially when inventory is not available
  useEffect(() => {
    if (accommodationDetail && accommodationDetail.currencyCode && typeof accommodationDetail.currencyCode === 'string') {
      const accommodationCurrency = accommodationDetail.currencyCode.toUpperCase();
      
      // Only set currency from accommodationDetail if:
      // 1. Currency hasn't been set from availability API yet (hasApiCurrency = false)
      // 2. OR current currency doesn't match accommodation currency (fallback case)
      if (!hasApiCurrency) {
        const currencySymbols = {
          'INR': '₹', 'USD': '$', 'EUR': '€', 'GBP': '£', 'AED': 'AED',
          'SAR': 'SAR', 'QAR': 'QAR', 'KWD': 'KWD', 'OMR': 'OMR', 'BHD': 'BHD',
          'PHP': '₱', 'THB': '฿', 'MYR': 'RM', 'SGD': 'S$', 'IDR': 'Rp',
          'PKR': 'Rs'
        };
        const currencyNames = {
          'INR': 'Indian Rupee', 'USD': 'US Dollar', 'EUR': 'Euro', 'GBP': 'British Pound',
          'AED': 'UAE Dirham', 'SAR': 'Saudi Riyal', 'QAR': 'Qatari Riyal',
          'KWD': 'Kuwaiti Dinar', 'OMR': 'Omani Rial', 'BHD': 'Bahraini Dinar',
          'PHP': 'Philippine Peso', 'THB': 'Thai Baht', 'MYR': 'Malaysian Ringgit',
          'SGD': 'Singapore Dollar', 'IDR': 'Indonesian Rupiah', 'PKR': 'Pakistani Rupee'
        };
        
        const currencyToSet = {
          code: accommodationCurrency,
          symbol: currencySymbols[accommodationCurrency] || accommodationCurrency,
          rate: 1,
          name: currencyNames[accommodationCurrency] || accommodationCurrency
        };
        
        setCurrentCurrency(currencyToSet);
        localStorage.setItem('selectedCurrency', JSON.stringify(currencyToSet));
        logger.debug('Currency set from accommodationDetail API:', accommodationCurrency);
      } else if (currentCurrency && currentCurrency.code !== accommodationCurrency) {
        // If availability API currency doesn't match accommodation currency, log for debugging
        logger.debug('Currency mismatch - Availability API:', currentCurrency.code, 'vs Accommodation API:', accommodationCurrency);
      }
    }
  }, [accommodationDetail, hasApiCurrency]);

  // Fetch addons from API when accommodationId or currency changes
  useEffect(() => {
    const fetchAddons = async () => {
      if (!accommodationId) {
        setAddOns([]);
        return;
      }

      setIsLoadingAddOns(true);
      setAddOnsError(null);

      // Priority: URL currency > RatePlanCurrencyCode > CurrencyCode
      const firstRatePlan = apiRooms?.[0]?.ratePlans?.[0];
      const addonCurrency = urlParams.currency ||
                            firstRatePlan?.ratePlanCurrencyCode ||
                            firstRatePlan?.RatePlanCurrencyCode ||
                            firstRatePlan?.CurrencyCode ||
                            firstRatePlan?.currency ||
                            '';

      try {
        const addonsData = await getAddonList({
          hotel_id: accommodationId,
          lang_id: currentLanguage?.id || currentLanguage?.lang_id || 1,
          currency: addonCurrency,
          nights: calculateNights(),
        });
        setAddOns(addonsData);
      } catch (error) {
        setAddOnsError(error.message);
        setAddOns([]); // Set empty array on error
      } finally {
        setIsLoadingAddOns(false);
      }
    };

    fetchAddons();
  }, [accommodationId, currentLanguage?.id || currentLanguage?.lang_id, currentCurrency?.code, urlParams.currency, apiRooms]);

  // Data completeness audit — agar hotel ka data adhura ho (tax/policy/photos/size missing),
  // detect karke backend ko report bhejo (per hotel per session 1 baar). Non-blocking.
  useEffect(() => {
    if (apiRooms && apiRooms.length > 0) {
      auditAndReportCompleteness(apiRooms, {
        hotelId: accommodationId || urlParams.hotel_id || '',
        hotelName: accommodationDetail?.accommodationName || accommodationDetail?.AccommodationName || '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiRooms, accommodationDetail]);

  // Use only API rooms - no hardcoded fallback
  const displayRooms = apiRooms;

  // ===== Lowest-price room toast =====
  // Show a small bottom-left toast when the user selects the cheapest available room.
  const [showLowestPriceToast, setShowLowestPriceToast] = useState(false);
  const lowestToastTimerRef = useRef(null);

  // Lowest available per-night room price across all rooms/rate plans shown.
  // Uses a rate-plan-based price (NOT loyalty/calculatedPrice) so comparisons are fair.
  const lowestAvailableRoomPrice = useMemo(() => {
    if (!displayRooms || displayRooms.length === 0) return null;
    let min = Infinity;
    displayRooms.forEach((room) => {
      (room.ratePlans || []).forEach((rp) => {
        let p = rp.price || (room.basePrice * (1 + (rp.priceModifier || 0)));
        if (rp.promotionDiscount?.discount > 0 && (rp.promotionDiscount?.promotionName || rp.promotionName)) {
          p = rp.promotionDiscount.afterDiscountAmount;
        }
        if (rp.secretDeal) p = p * (1 - (rp.memberDiscount || 0));
        const n = parseFloat(p);
        if (!isNaN(n) && n > 0 && n < min) min = n;
      });
    });
    return min === Infinity ? null : min;
  }, [displayRooms]);

  const triggerLowestPriceToast = () => {
    setShowLowestPriceToast(true);
    if (lowestToastTimerRef.current) clearTimeout(lowestToastTimerRef.current);
    lowestToastTimerRef.current = setTimeout(() => setShowLowestPriceToast(false), 4000);
  };

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [modalCurrentImageIndex, setModalCurrentImageIndex] = useState(0);
  const [showGuestSelector, setShowGuestSelector] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [selectedAddOnForComment, setSelectedAddOnForComment] = useState(null);
  const [addOnComments, setAddOnComments] = useState({});

  // Close guest selector when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showGuestSelector &&
        !event.target.closest(".guest-selector-container")
      ) {
        setShowGuestSelector(false);
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === "Escape" && showGuestSelector) {
        setShowGuestSelector(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscapeKey);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [showGuestSelector]);

  // Calculate functions
  const calculateNights = () => {
    const checkIn = new Date(selectedDates.checkIn);
    const checkOut = new Date(selectedDates.checkOut);
    const timeDiff = checkOut.getTime() - checkIn.getTime();
    return Math.ceil(timeDiff / (1000 * 3600 * 24));
  };

  const calculateTotal = () => {
    const nights = calculateNights();
    let roomSubtotal = 0;
    let totalTaxes = 0;
    let addOnsSubtotal = 0;

    // Calculate room costs (same as AvailabilityComponent.jsx)
    selectedRooms.forEach((item) => {
      // Use calculatedPrice if available (includes loyalty discount), otherwise calculate
      let roomPrice = item.calculatedPrice;
      if (!roomPrice) {
        // Fallback: calculate price if calculatedPrice not stored
        roomPrice = item.ratePlan.price
          ? item.ratePlan.price
          : item.room.basePrice * (1 + (item.ratePlan.priceModifier || 0));

        // Apply promotion discount if available - Promotion only applies if both name and discount value are present
        if (item.ratePlan.promotionDiscount &&
            item.ratePlan.promotionDiscount.discount > 0 &&
            (item.ratePlan.promotionDiscount.promotionName || item.ratePlan.promotionName)) {
          roomPrice = item.ratePlan.promotionDiscount.afterDiscountAmount;
        }

        if (item.ratePlan.secretDeal) {
          roomPrice = roomPrice * (1 - item.ratePlan.memberDiscount);
        }
      }
      // Multiply by quantity (NOT nights - price is per night, total is calculated elsewhere)
      const itemTotal = roomPrice * item.quantity;
      roomSubtotal += itemTotal;

      // Calculate taxes directly from ratePlan.taxes (same logic as tax modal)
      // This ensures booking summary matches tax modal exactly
      if (item.ratePlan.taxes && Array.isArray(item.ratePlan.taxes)) {
        // Calculate from ratePlan.taxes (from tax API response)
        const roomTax = item.ratePlan.taxes.reduce((taxSum, tax) => {
          if (isTaxIncluded(tax)) return taxSum; // Included tax = already in rate, don't add
          const taxId = tax.TaxId || tax.taxId || tax.TaxID || 0;
          let taxAmt = 0;
          // TaxId = 19: TaxValue (as-is, no quantity multiplication)
          // Other taxes: TaxAmmount (direct)
          if (taxId === 19) {
            taxAmt = parseFloat(tax.TaxValue || tax.taxValue || 0);
          } else {
            taxAmt = parseFloat(tax.TaxAmmount || tax.TaxAmount || tax.taxAmount || 0);
          }
          return taxSum + (isNaN(taxAmt) ? 0 : taxAmt);
        }, 0);
        totalTaxes += roomTax;
      } else if (item.taxAmount && typeof item.taxAmount === 'number' && !isNaN(item.taxAmount) && item.taxAmount > 0) {
        // Fallback: use stored taxAmount from when room was selected
        totalTaxes += item.taxAmount;
      } else {
        // Last fallback: calculate from room.taxes (old structure)
        let roomTax = 0;
        if (item.room.taxes) {
          if (typeof item.room.taxes === 'number') {
            roomTax = item.room.taxes;
          } else if (typeof item.room.taxes === 'object') {
            const taxObj = item.room.taxes;
            if (taxObj.Tax && Array.isArray(taxObj.Tax)) {
              roomTax = taxObj.Tax.reduce((taxSum, tax) => {
                if (isTaxIncluded(tax)) return taxSum; // Included tax = already in rate, don't add
                const taxId = tax.TaxId || tax.taxId || tax.TaxID || 0;
                let taxAmt = 0;
                if (taxId === 19) {
                  taxAmt = parseFloat(tax.TaxValue || tax.taxValue || 0);
                } else {
                  taxAmt = parseFloat(tax.TaxAmmount || tax.TaxAmount || tax.taxAmount || 0);
                }
                return taxSum + (isNaN(taxAmt) ? 0 : taxAmt);
              }, 0);
            } else if (taxObj.Tax && typeof taxObj.Tax === 'object') {
              const tax = taxObj.Tax;
              const taxId = tax.TaxId || tax.taxId || tax.TaxID || 0;
              if (isTaxIncluded(tax)) {
                roomTax = 0; // Included tax = already in rate, don't add
              } else if (taxId === 19) {
                roomTax = parseFloat(tax.TaxValue || tax.taxValue || 0);
              } else {
                roomTax = parseFloat(tax.TaxAmmount || tax.TaxAmount || tax.taxAmount || 0);
              }
            }
          }
        }
        totalTaxes += roomTax;
      }
    });

    // Calculate add-ons costs
    selectedAddOns.forEach((item) => {
      const addonPrice = item.option?.price || item.addOn.price;
      const nightsMultiplier = String(item.addOn?.activityId) === '4' ? nights : 1;
      addOnsSubtotal += addonPrice * item.quantity * nightsMultiplier;
    });

    const subtotal = roomSubtotal + addOnsSubtotal;
    const total = subtotal + totalTaxes;

    return {
      roomSubtotal,
      addOnsSubtotal,
      subtotal,
      taxes: totalTaxes,
      total,
      nights,
    };
  };

  // Room management functions
  const removeRoom = (roomId, ratePlanId) => {
    setSelectedRooms(
      selectedRooms.filter(
        (item) => !(item.room.id === roomId && item.ratePlan.id === ratePlanId),
      ),
    );
  };

  const removeAddOn = (addOnId) => {
    setSelectedAddOns(
      selectedAddOns.filter((item) => item.addOn.id !== addOnId),
    );
  };

  const updateRoomQuantity = async (roomId, ratePlanId, newQuantity, calculatedPrice = null) => {
    if (newQuantity <= 0) {
      removeRoom(roomId, ratePlanId);
      return;
    }

    const room = displayRooms.find((r) => r.id === roomId);
    const ratePlan = room?.ratePlans.find((rp) => rp.id === ratePlanId);

    if (!room || !ratePlan) return;

    // Calculate total quantity selected for this room across all rate plans
    const currentQuantityForThisPlan = selectedRooms.find(
      (item) => item.room.id === roomId && item.ratePlan.id === ratePlanId
    )?.quantity || 0;
    
    const totalSelectedForRoom = selectedRooms
      .filter((item) => item.room.id === roomId)
      .reduce((sum, item) => sum + item.quantity, 0);
    
    // Get available rooms for this room type
    const roomsAvailable = room.totalroom || 0;
    
    // Calculate what the new total would be
    const newTotalForRoom = totalSelectedForRoom - currentQuantityForThisPlan + newQuantity;
    
    // Check if we can add more rooms
    if (newTotalForRoom > roomsAvailable) {
      alert(`Only ${roomsAvailable} room${roomsAvailable > 1 ? 's' : ''} available for ${room.name}`);
      return;
    }

    // Calculate room price first (needed for both tax calculation and storing in selectedRooms)
    // Use calculatedPrice if provided (includes loyalty discount), otherwise calculate
    let roomPrice = calculatedPrice || ratePlan.price || (room.basePrice * (1 + (ratePlan.priceModifier || 0)));
    if (!calculatedPrice) {
      // Only recalculate if calculatedPrice not provided
      // Promotion only applies if both name and discount value are present
      if (ratePlan.promotionDiscount?.discount > 0 && 
          (ratePlan.promotionDiscount?.promotionName || ratePlan.promotionName)) {
        roomPrice = ratePlan.promotionDiscount.afterDiscountAmount;
      }
      if (ratePlan.secretDeal) {
        roomPrice = roomPrice * (1 - (ratePlan.memberDiscount || 0));
      }
    }
    
    // If the user picked the cheapest available room, celebrate with a toast.
    // Use a rate-plan-based comparable price (NOT calculatedPrice/loyalty) so it
    // matches lowestAvailableRoomPrice.
    let comparablePrice = ratePlan.price || (room.basePrice * (1 + (ratePlan.priceModifier || 0)));
    if (ratePlan.promotionDiscount?.discount > 0 && (ratePlan.promotionDiscount?.promotionName || ratePlan.promotionName)) {
      comparablePrice = ratePlan.promotionDiscount.afterDiscountAmount;
    }
    if (ratePlan.secretDeal) comparablePrice = comparablePrice * (1 - (ratePlan.memberDiscount || 0));
    comparablePrice = parseFloat(comparablePrice) || 0;
    if (lowestAvailableRoomPrice != null && comparablePrice > 0 && comparablePrice <= lowestAvailableRoomPrice + 0.5) {
      triggerLowestPriceToast();
    }

    // Calculate tax for this room selection
    let calculatedTax = 0;
    let taxBreakdown = null; // Store tax breakdown from API
    try {
      const { getTax, extractTaxDataFromRatePlan, isTaxIncluded } = await import('../services/taxApi');
      const nights = calculateNights();

      // Extract tax data from rate plan
      const taxArray = extractTaxDataFromRatePlan(ratePlan);

      if (taxArray && taxArray.length > 0) {
        // Calculate total price: roomPrice * quantity (same as RatePlanCard tax modal)
        const totalPrice = roomPrice * newQuantity;
        const noofroomArray = { "0": newQuantity };
        const totalBedrooms = (ratePlan.noOfBedRooms || 1);

        // Call tax API
        const taxResult = await getTax({
          nights,
          conversionrate: ratePlan.conversionRate || 0,
          noofroomArray,
          totalnumberofbedrooms: totalBedrooms,
          price: totalPrice,
          data: taxArray
        });

        // Store tax breakdown from API response
        taxBreakdown = taxResult.taxesBreakDown || taxResult.taxes || [];

        // Extract total tax from response - same logic as RatePlanCard tax modal
        if (Array.isArray(taxBreakdown) && taxBreakdown.length > 0) {
          calculatedTax = taxBreakdown.reduce((sum, tax) => {
            if (isTaxIncluded(tax)) return sum; // Included tax = already in rate, don't add
            const taxId = tax.TaxId || tax.taxId || tax.TaxID || 0;
            // For TaxId = 19, use TaxValue as-is (no quantity multiplication)
            // For other taxes, use TaxAmmount (direct from API)
            let taxAmt = 0;
            if (taxId === 19) {
              const taxValue = parseFloat(tax.TaxValue || tax.taxValue || 0);
              taxAmt = taxValue;
            } else {
              taxAmt = parseFloat(tax.TaxAmmount || tax.TaxAmount || tax.taxAmount || tax.amount || 0);
            }
            return sum + (isNaN(taxAmt) ? 0 : taxAmt);
          }, 0);
        } else {
          calculatedTax = parseFloat(taxResult.totalTax || taxResult.TotalTax || taxResult.totalTaxAmount || 0);
        }
      }
    } catch (error) {
      // Continue without tax if calculation fails
    }

    const existingItemIndex = selectedRooms.findIndex(
      (item) => item.room.id === roomId && item.ratePlan.id === ratePlanId,
    );

    // Store calculatedPrice (with loyalty discount) in selectedRooms
    const finalPrice = calculatedPrice || roomPrice;

    // Create updated ratePlan with tax breakdown from API
    const updatedRatePlan = taxBreakdown ? { ...ratePlan, taxes: taxBreakdown } : ratePlan;

    if (existingItemIndex === -1) {
      setSelectedRooms([
        ...selectedRooms,
        { room, ratePlan: updatedRatePlan, quantity: newQuantity, taxAmount: calculatedTax, calculatedPrice: finalPrice },
      ]);
    } else {
      const updated = [...selectedRooms];
      updated[existingItemIndex].quantity = newQuantity;
      updated[existingItemIndex].taxAmount = calculatedTax;
      updated[existingItemIndex].calculatedPrice = finalPrice;
      updated[existingItemIndex].ratePlan = updatedRatePlan;
      setSelectedRooms(updated);
    }
  };

  // Add-on management functions
  const updateAddOnQuantity = (addOnId, optionId, newQuantity) => {
    if (newQuantity <= 0) {
      setSelectedAddOns(
        selectedAddOns.filter((item) => item.addOn.id !== addOnId),
      );
      return;
    }

    // Find the add-on to check MaxQty
    const addOn = addOns.find((a) => a.id === addOnId);
    if (addOn) {
      const maxQty = addOn.maxQty || 5;
      if (newQuantity > maxQty) {
        alert(`Maximum quantity is ${maxQty} for ${addOn.name}.`);
        return;
      }
    }

    const existingAddOnIndex = selectedAddOns.findIndex(
      (item) => item.addOn.id === addOnId,
    );

    if (existingAddOnIndex === -1) {
      if (addOn) {
        setSelectedAddOns([
          ...selectedAddOns,
          {
            addOn,
            option: {
              id: "default",
              name: addOn.name,
              price: addOn.price,
            },
            quantity: newQuantity,
            // Store add-on IDs for reservation API
            id: addOn.id,
            activityId: addOn.activityId || addOn.id,
            variationId: addOn.variationId || addOn.id,
            qty: newQuantity,
            comment: addOnComments[addOn.id] || '',
          },
        ]);
      }
    } else {
      const updated = [...selectedAddOns];
      updated[existingAddOnIndex].quantity = newQuantity;
      updated[existingAddOnIndex].qty = newQuantity;
      updated[existingAddOnIndex].comment = addOnComments[addOn.id] || '';
      setSelectedAddOns(updated);
    }
  };

  // Modal management functions
  const openRoomModal = (room) => {
    setSelectedRoom(room);
    setModalCurrentImageIndex(0);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedRoom(null);
  };

  const openCommentModal = (addOn) => {
    setSelectedAddOnForComment(addOn);
    setShowCommentModal(true);
  };

  const closeCommentModal = () => {
    setShowCommentModal(false);
    setSelectedAddOnForComment(null);
  };

  const saveComment = (addOnId, comment) => {
    setAddOnComments((prev) => ({ ...prev, [addOnId]: comment }));
    
    // Update comment in selectedAddOns if add-on is already selected
    setSelectedAddOns((prev) => {
      return prev.map((item) => {
        if (item.addOn.id === addOnId) {
          return {
            ...item,
            comment: comment || '',
          };
        }
        return item;
      });
    });
    
    closeCommentModal();
  };

  // Checkout functions
  const startCheckout = () => {
    if (selectedRooms.length === 0 && selectedAddOns.length === 0) {
      alert("Please select at least one room or add-on to proceed with checkout.");
      return;
    }
    // Only validate dates if rooms are selected (addons can be booked without dates)
    if (selectedRooms.length > 0 && selectedDates.checkIn >= selectedDates.checkOut) {
      alert("Check-out date must be after check-in date.");
      return;
    }

    // If on rooms tab and add-ons are available, switch to add-ons tab first
    if (activeTab === "rooms" && Array.isArray(addOns) && addOns.length > 0) {
      setActiveTab("addons");
      // Scroll to top of the content area
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Prepare URL parameters for checkout page (YYYY-MM-DD for unambiguous URLs)
    const checkinURL = formatDate(selectedDates.checkIn);
    const checkoutURL = formatDate(selectedDates.checkOut);
    const accId = accommodationId || urlParams.hotel_id || urlParams.accommodationid || '';

    const checkoutParams = new URLSearchParams();
    checkoutParams.set('hotel_id', accId);
    checkoutParams.set('checkin', checkinURL);
    checkoutParams.set('checkout', checkoutURL);
    checkoutParams.set('adult', guests.adults.toString());
    checkoutParams.set('child', guests.children.toString());
    
    // Add child ages as comma-separated string
    if (childAges.length > 0) {
      checkoutParams.set('childAges', childAges.join(','));
    }
    
    // Preserve optional parameters if they exist
    const promoForCheckout = (promoCode || '').trim() || urlParams.promo;
    if (promoForCheckout) {
      checkoutParams.set('promo', promoForCheckout);
    }
    if (urlParams.profileid) {
      checkoutParams.set('profileid', urlParams.profileid);
    }
    if (urlParams.multilanguageid) {
      checkoutParams.set('multilanguageid', urlParams.multilanguageid.toString());
    }
    if (urlParams.currency) {
      checkoutParams.set('currency', urlParams.currency);
    }

    // Preserve tracking parameters (utm_campaign, utm_medium, utm_source, etc.)
    const utmCampaign = searchParams.get('utm_campaign');
    const utmMedium = searchParams.get('utm_medium');
    const utmSource = searchParams.get('utm_source');
    const gaParam = searchParams.get('_ga');

    if (utmCampaign) checkoutParams.set('utm_campaign', utmCampaign);
    if (utmMedium) checkoutParams.set('utm_medium', utmMedium);
    if (utmSource) checkoutParams.set('utm_source', utmSource);
    if (gaParam) checkoutParams.set('_ga', gaParam);

    // Add session tracking params
    const sessionId = getSessionId();
    const wcid = searchParams.get('wcid') || '';
    const domain = searchParams.get('domain') || '';
    const lang = searchParams.get('lang') || urlParams.lang || 'en';

    checkoutParams.set('sessionId', sessionId);
    if (wcid) checkoutParams.set('wcid', wcid);
    if (domain) checkoutParams.set('domain', domain);

    // Transform selectedAddOns to include id, variationId, qty, and comment for reservation API
    const transformedSelectedAddOns = selectedAddOns.map((item) => {
      const addOn = item.addOn;
      return {
        id: item.id || addOn.id,
        activityId: item.activityId || addOn.activityId || addOn.id,
        variationId: item.variationId || addOn.variationId || addOn.id,
        qty: item.qty || item.quantity,
        comment: item.comment || addOnComments[addOn.id] || '',
        name: addOn.name,
        price: addOn.price,
        // Keep original structure for backward compatibility
        addOn: addOn,
        option: item.option,
        quantity: item.quantity,
      };
    });
    
    // Navigate to unified checkout with booking data and URL parameters
    const bookingData = {
      hotelName: accommodationDetail?.accommodationName,
      hotelImage: accommodationDetail?.hotelImages?.[0],
      hotelAddress: accommodationDetail?.Address,
      checkIn: selectedDates.checkIn,
      checkOut: selectedDates.checkOut,
      adults: guests.adults,
      children: guests.children,
      rooms: selectedRooms.reduce((sum, item) => sum + item.quantity, 0),
      selectedRoom: selectedRooms[0]?.room || displayRooms[0] || null,
      selectedRatePlan: selectedRooms[0]?.ratePlan?.name || '',
      totalAmount: total,
      nights: calculateNights(),
      selectedRooms: selectedRooms,
      selectedAddOns: transformedSelectedAddOns,
      roomSubtotal: roomSubtotal,
      addOnsSubtotal: addOnsSubtotal,
      taxes: taxes,
      currency: currentCurrency?.code || selectedRooms[0]?.ratePlan?.currency || selectedRooms[0]?.ratePlan?.CurrencyCode || accommodationDetail?.CurrencyCode || accommodationDetail?.currencyCode || ''
    };
    
    // Analytics: track room selected
    trackRoomSelected(accId, {
      checkin: checkinURL,
      checkout: checkoutURL,
      adults: guests.adults,
      children: guests.children,
      rooms: selectedRooms.map(item => ({
        roomId: item.room?.RoomID || item.room?.id,
        roomName: item.room?.RoomName || item.room?.name,
        ratePlanId: item.ratePlan?.RatePlanID || item.ratePlan?.id,
        ratePlanName: item.ratePlan?.RatePlanName || item.ratePlan?.name,
        quantity: item.quantity,
        price: item.ratePlan?.price || item.calculatedPrice,
        currency: currentCurrency?.code || item.ratePlan?.currency || ''
      })),
      totalAmount: total,
      currency: currentCurrency?.code || selectedRooms[0]?.ratePlan?.currency || ''
    });

    navigate(`/checkout-page?${checkoutParams.toString()}`, { state: bookingData });
  };

  const handleBackToSummary = () => setShowCheckout(false);
  const handleCheckoutComplete = (confirmation) => {
    // Handle checkout completion
  };

  // Fetch room availability from API
  const handleSearchAvailability = async () => {
    if (!selectedDates.checkIn || !selectedDates.checkOut) {
      alert("Please select check-in and check-out dates");
      return;
    }

    if (guests.adults < 1) {
      alert("Please select at least 1 adult");
      return;
    }

    setIsLandingMode(false); // user is searching → leave landing
    setIsLoadingRooms(true);
    setRoomsError(null);
    setHasSearched(true);
    setDisplayedRoomsCount(2); // Reset to initial count for new search

    // Analytics: track search
    const accId = accommodationId || searchParams.get('hotel_id') || '';
    trackSearch(accId, {
      checkin: formatDate(selectedDates.checkIn),
      checkout: formatDate(selectedDates.checkOut),
      adults: guests.adults,
      children: guests.children,
      promoCode: (promoCode || '').trim() || null
    });

    try {
      const checkinForAPI = formatDateForAPI(selectedDates.checkIn);  // DD-MM-YYYY for API
      const checkoutForAPI = formatDateForAPI(selectedDates.checkOut);
      const checkinForURL = formatDate(selectedDates.checkIn);  // YYYY-MM-DD for URL (unambiguous)
      const checkoutForURL = formatDate(selectedDates.checkOut);
      const accId = accommodationId || urlParams.hotel_id || urlParams.accommodationid || '';

      // Update URL parameters when search is performed
      // Start with existing search params to preserve all parameters
      const newSearchParams = new URLSearchParams(searchParams);

      // Update required parameters - use YYYY-MM-DD in URL (unambiguous, works with Google & all sources)
      newSearchParams.set('hotel_id', accId);
      newSearchParams.set('checkin', checkinForURL);
      newSearchParams.set('checkout', checkoutForURL);
      newSearchParams.set('adult', guests.adults.toString());
      newSearchParams.set('child', guests.children.toString());

      // Add child ages as comma-separated string to URL
      if (childAges.length > 0) {
        newSearchParams.set('childAges', childAges.join(','));
      } else {
        // Remove childAges if empty
        newSearchParams.delete('childAges');
      }

      // Promo code — use the typed value (falls back to URL param)
      const promoForSearch = (promoCode || '').trim();
      if (promoForSearch) {
        newSearchParams.set('promo', promoForSearch);
      } else {
        newSearchParams.delete('promo');
      }
      if (urlParams.profileid) {
        newSearchParams.set('profileid', urlParams.profileid);
      }
      if (urlParams.multilanguageid) {
        newSearchParams.set('multilanguageid', urlParams.multilanguageid.toString());
      }

      // Preserve currency parameter ONLY if it exists in URL (user has changed currency)
      // Don't add currency parameter if it's not in URL
      if (urlParams.currency) {
        newSearchParams.set('currency', urlParams.currency);
      }

      // Update URL without page reload
      setSearchParams(newSearchParams, { replace: true });

      const params = {
        hotel_id: accId,
        checkin: checkinForAPI,
        checkout: checkoutForAPI,
        adult: guests.adults,
        child: guests.children,
        childAges: childAges.length > 0 ? childAges.join(',') : '', // Comma-separated child ages
        promo: promoForSearch,
        profileid: urlParams.profileid || '',
        multilanguageid: searchParams.get('multilanguageid') || localStorage.getItem('multilanguageid') || urlParams.multilanguageid || 1,
        currency: urlParams.currency || '',
        device: 'desktop',
        ratecode: urlParams.ratecode || ''
      };

      const apiData = await fetchRoomAvailability(params);

      const transformedRooms = transformApiRoomsToLocalFormat(apiData);

      logger.debug('Inventory data loaded', {
        totalRooms: transformedRooms?.length || 0,
        apiDataType: typeof apiData,
        hasData: !!apiData
      });
      
      // Extract currency from API response (priority: Availability API > Accommodation API)
      let currencyExtracted = false;
      
      if (transformedRooms && transformedRooms.length > 0) {
        const firstRoom = transformedRooms[0];
        if (firstRoom.ratePlans && firstRoom.ratePlans.length > 0) {
          const firstRatePlan = firstRoom.ratePlans[0];
          
          // Priority: CurrencyCode (from availabilityApi.js) > currency > convertedCurrency > ratePlanCurrencyCode
          // Check each field and use the first non-empty value
          let apiCurrency = null;
          // First priority: CurrencyCode directly from ratePlan (as set in availabilityApi.js)
          if (firstRatePlan.CurrencyCode && String(firstRatePlan.CurrencyCode).trim()) {
            apiCurrency = String(firstRatePlan.CurrencyCode).trim();
          } else if (firstRatePlan.currency && String(firstRatePlan.currency).trim()) {
            // Second priority: currency field directly in ratePlan object (which comes from CurrencyCode in availabilityApi.js)
            apiCurrency = String(firstRatePlan.currency).trim();
          } else if (firstRatePlan.convertedCurrency && String(firstRatePlan.convertedCurrency).trim()) {
            apiCurrency = String(firstRatePlan.convertedCurrency).trim();
          } else if (firstRatePlan.ratePlanCurrencyCode && String(firstRatePlan.ratePlanCurrencyCode).trim()) {
            apiCurrency = String(firstRatePlan.ratePlanCurrencyCode).trim();
          }
          
          if (apiCurrency) {
            // Always update currency from API response (API has highest priority)
            const currencySymbols = {
              'INR': '₹', 'USD': '$', 'EUR': '€', 'GBP': '£', 'AED': 'AED',
              'SAR': 'SAR', 'QAR': 'QAR', 'KWD': 'KWD', 'OMR': 'OMR', 'BHD': 'BHD',
              'PHP': '₱', 'THB': '฿', 'MYR': 'RM', 'SGD': 'S$', 'IDR': 'Rp',
              'PKR': 'Rs'
            };
            const currencyNames = {
              'INR': 'Indian Rupee', 'USD': 'US Dollar', 'EUR': 'Euro', 'GBP': 'British Pound',
              'AED': 'UAE Dirham', 'SAR': 'Saudi Riyal', 'QAR': 'Qatari Riyal',
              'KWD': 'Kuwaiti Dinar', 'OMR': 'Omani Rial', 'BHD': 'Bahraini Dinar',
              'PHP': 'Philippine Peso', 'THB': 'Thai Baht', 'MYR': 'Malaysian Ringgit',
              'SGD': 'Singapore Dollar', 'IDR': 'Indonesian Rupiah', 'PKR': 'Pakistani Rupee'
            };
            
            const currencyToSet = {
              code: apiCurrency.toUpperCase(),
              symbol: currencySymbols[apiCurrency.toUpperCase()] || apiCurrency,
              rate: firstRatePlan.conversionRate || 1,
              name: currencyNames[apiCurrency.toUpperCase()] || apiCurrency
            };
            
            setCurrentCurrency(currencyToSet);
            setHasApiCurrency(true); // Mark that we have API currency
            localStorage.setItem('selectedCurrency', JSON.stringify(currencyToSet));
            currencyExtracted = true;
            
            // Don't add currency to URL automatically from API
            // Currency parameter will only be added when user manually changes currency
          }
        }
      }
      
      // Fallback: If currency not extracted from availability API, use accommodationDetail API
      if (!currencyExtracted && accommodationDetail && accommodationDetail.currencyCode && typeof accommodationDetail.currencyCode === 'string') {
        const accommodationCurrency = accommodationDetail.currencyCode.toUpperCase();

        const currencySymbols = {
          'INR': '₹', 'USD': '$', 'EUR': '€', 'GBP': '£', 'AED': 'AED',
          'SAR': 'SAR', 'QAR': 'QAR', 'KWD': 'KWD', 'OMR': 'OMR', 'BHD': 'BHD',
          'PHP': '₱', 'THB': '฿', 'MYR': 'RM', 'SGD': 'S$', 'IDR': 'Rp',
          'PKR': 'Rs'
        };
        const currencyNames = {
          'INR': 'Indian Rupee', 'USD': 'US Dollar', 'EUR': 'Euro', 'GBP': 'British Pound',
          'AED': 'UAE Dirham', 'SAR': 'Saudi Riyal', 'QAR': 'Qatari Riyal',
          'KWD': 'Kuwaiti Dinar', 'OMR': 'Omani Rial', 'BHD': 'Bahraini Dinar',
          'PHP': 'Philippine Peso', 'THB': 'Thai Baht', 'MYR': 'Malaysian Ringgit',
          'SGD': 'Singapore Dollar', 'IDR': 'Indonesian Rupiah', 'PKR': 'Pakistani Rupee'
        };
        
        const currencyToSet = {
          code: accommodationCurrency,
          symbol: currencySymbols[accommodationCurrency] || accommodationCurrency,
          rate: 1,
          name: currencyNames[accommodationCurrency] || accommodationCurrency
        };
        
        setCurrentCurrency(currencyToSet);
        localStorage.setItem('selectedCurrency', JSON.stringify(currencyToSet));
      }
      
      // Store all rooms
      setAllApiRooms(transformedRooms);
      
      // Display initial batch (first 5 rooms for fast initial load)
      const initialBatch = transformedRooms.slice(0, 5);
      setApiRooms(initialBatch);
      setDisplayedRoomsCount(5);

      // Analytics: track availability results
      const availableRooms = transformedRooms.filter(r => r.totalroom > 0 || r.ratePlans?.length > 0).length;
      trackAvailabilityResults(accId, {
        checkin: formatDate(selectedDates.checkIn),
        checkout: formatDate(selectedDates.checkOut),
        adults: guests.adults,
        children: guests.children,
        totalRooms: transformedRooms.length,
        availableRooms,
        soldOut: transformedRooms.length === 0
      });
    } catch (error) {
      const errorMessage = error.message || "Failed to fetch room availability";
      setRoomsError(errorMessage);
      alert(errorMessage);
    } finally {
      setIsLoadingRooms(false);
    }
  };

  // Load more rooms function - using useCallback to avoid dependency issues
  const loadMoreRooms = useCallback(() => {
    if (isLoadingMore || displayedRoomsCount >= allApiRooms.length) return;
    
    setIsLoadingMore(true);
    
    // Load next batch (5 more rooms)
    setTimeout(() => {
      const nextCount = Math.min(displayedRoomsCount + 5, allApiRooms.length);
      const nextBatch = allApiRooms.slice(0, nextCount);
      setApiRooms(nextBatch);
      setDisplayedRoomsCount(nextCount);
      setIsLoadingMore(false);
    }, 200);
  }, [isLoadingMore, displayedRoomsCount, allApiRooms]);

  // Scroll detection for infinite scroll
  useEffect(() => {
    if (!hasSearched || allApiRooms.length <= displayedRoomsCount || isLoadingMore) return;

    const handleScroll = () => {
      const scrollPosition = window.innerHeight + window.scrollY;
      const documentHeight = document.documentElement.scrollHeight;
      
      // Load more when user is 300px from bottom
      if (scrollPosition >= documentHeight - 300) {
        loadMoreRooms();
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasSearched, allApiRooms.length, displayedRoomsCount, isLoadingMore, loadMoreRooms]);

  // Auto-select first room when tracking params (utm_campaign=HotelAds) are present
  useEffect(() => {
    // Only auto-select if:
    // 1. User came from HotelAds (utm_campaign=HotelAds)
    // 2. Rooms are loaded
    // 3. No room is already selected
    if (isFromHotelAds() && apiRooms.length > 0 && selectedRooms.length === 0) {
      const firstRoom = apiRooms[0];
      const firstRatePlan = firstRoom?.ratePlans?.[0];

      if (firstRoom && firstRatePlan) {
        // Apply same discount logic as normal selection (updateRoomQuantity)
        let autoPrice = firstRatePlan.price || firstRoom.basePrice;
        if (firstRatePlan.promotionDiscount?.discount > 0 &&
            (firstRatePlan.promotionDiscount?.promotionName || firstRatePlan.promotionName)) {
          autoPrice = firstRatePlan.promotionDiscount.afterDiscountAmount;
        }
        if (firstRatePlan.secretDeal) {
          autoPrice = autoPrice * (1 - (firstRatePlan.memberDiscount || 0));
        }

        setSelectedRooms([
          {
            room: firstRoom,
            ratePlan: firstRatePlan,
            quantity: 1,
            taxAmount: 0,
            calculatedPrice: autoPrice
          }
        ]);
      }
    }
  }, [apiRooms]); // Run when rooms are loaded

  // Update child ages from URL parameters when they change
  useEffect(() => {
    const urlChildAges = searchParams.get('childAges');
    const urlChildren = searchParams.get('child');
    
    if (urlChildAges) {
      // Parse child ages from comma-separated string
      const parsedAges = urlChildAges.split(',').map(age => parseInt(age.trim())).filter(age => !isNaN(age));
      const numChildren = urlChildren ? parseInt(urlChildren) : 0;
      
      // Update child ages if URL has valid ages
      if (parsedAges.length === numChildren && numChildren > 0) {
        setChildAges(parsedAges);
      } else if (numChildren > 0 && parsedAges.length !== numChildren) {
        // If children count changed but ages don't match, adjust ages array
        if (parsedAges.length < numChildren) {
          // Add default age 1 for missing children
          setChildAges([...parsedAges, ...Array(numChildren - parsedAges.length).fill(1)]);
        } else {
          // Remove extra ages
          setChildAges(parsedAges.slice(0, numChildren));
        }
      }
    } else if (urlChildren) {
      // If URL has children but no ages, initialize with default ages
      const numChildren = parseInt(urlChildren) || 0;
      if (numChildren > 0 && childAges.length !== numChildren) {
        setChildAges(Array(numChildren).fill(1));
      }
    }
  }, [searchParams]); // Run when URL params change

  // Auto-search on mount with default dates (today and tomorrow) if hotel_id is available
  useEffect(() => {
    // Check if hotel_id is available
    if (!hasHotelId) {
      return; // Don't search if no hotel_id
    }

    const checkin = searchParams.get('checkin');
    const checkout = searchParams.get('checkout');
    const adult = searchParams.get('adult');
    
    // checkin & checkout in URL → auto-search. Otherwise → LANDING MODE: show the
    // search form centered, no auto-search (today/tomorrow usually has no inventory
    // → avoids a false "no rooms" on first load). User picks dates themselves.
    if (checkin && checkout) {
      handleSearchAvailability();
    } else {
      setIsLandingMode(true);
      setIsLoadingRooms(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasHotelId]); // Run when hotel_id becomes available

  const { roomSubtotal, addOnsSubtotal, subtotal, taxes, total, nights } =
    calculateTotal();

  // Track if initial page load is complete (accommodation detail loaded)
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  
  // Check if initial load is complete (accommodation detail AND first room search must be done)
  useEffect(() => {
    if (hasHotelId && (accommodationDetail || accommodationGroupDetail) && !isLoadingAccommodation && !isLoadingGroup && !isLoadingRooms) {
      setIsInitialLoadComplete(true);
    }
  }, [hasHotelId, accommodationDetail, accommodationGroupDetail, isLoadingAccommodation, isLoadingGroup, isLoadingRooms]);

  // If no hotel_id, show message instead of full page
  if (!hasHotelId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-12 text-center">
          <div className="mb-8">
            <svg className="mx-auto h-24 w-24 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Please Select Hotel</h2>
          <p className="text-lg text-gray-600 mb-8">Please select a hotel to view room availability and make a booking.</p>
          <div className="flex items-center justify-center space-x-2 text-gray-500">
            <i className="fas fa-info-circle"></i>
            <span>Hotel ID is required to proceed</span>
          </div>
        </div>
      </div>
    );
  }

  // Show single full page loader until accommodation, rooms, AND theme CSS are all loaded
  if (!isInitialLoadComplete || isLoadingAccommodation || isLoadingGroup || !isThemeLoaded) {
    return (
      <div className="fixed inset-0 bg-white z-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-600 mb-4"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-loyalty-tick={loyaltyTick}>
      {/* Lowest-price room toast (bottom-left) */}
      {showLowestPriceToast && (
        <>
          <style>{`@keyframes lpToastIn{0%{transform:translateX(-120%);opacity:0}60%{transform:translateX(6%)}100%{transform:translateX(0);opacity:1}}`}</style>
          <div
            style={{ animation: 'lpToastIn 0.45s cubic-bezier(0.16,1,0.3,1)' }}
            className="fixed left-3 bottom-4 z-[9999] max-w-[240px]"
            role="status"
          >
            <div className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-emerald-500 to-green-600 px-3 py-2 text-white shadow-lg ring-1 ring-white/20">
              <span className="text-base">🎉</span>
              <div className="leading-tight">
                <p className="text-xs font-semibold">Great pick!</p>
                <p className="text-[11px] text-white/90">You selected the lowest-priced room.</p>
              </div>
              <button
                type="button"
                aria-label="Close"
                onClick={() => {
                  if (lowestToastTimerRef.current) clearTimeout(lowestToastTimerRef.current);
                  setShowLowestPriceToast(false);
                }}
                className="ml-0.5 -mr-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-sm leading-none text-white/80 transition hover:bg-white/20 hover:text-white"
              >
                ×
              </button>
            </div>
          </div>
        </>
      )}
      {/* Top contact strip — official website / best rate + phone + email (real data only) */}
      <TopContactBar accommodationDetail={accommodationDetail} />
      {/* Header */}
      <Header
        showCheckout={showCheckout}
        handleBackToSummary={handleBackToSummary}
        setShowSignInModal={setShowSignInModal}
        showSignInModal={showSignInModal}
        logo2000={accommodationDetail?.logoURL || ''}
        navigate={navigate}
        accommodationName={accommodationDetail?.accommodationName}
        currentLanguage={currentLanguage}
        onLanguageChange={async (language) => {
          localStorage.setItem('selectedLanguage', JSON.stringify(language));
          localStorage.setItem('multilanguageid', (language.id || 1).toString());
          // Update URL without page reload
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.set('multilanguageid', (language.id || 1).toString());
          setSearchParams(newSearchParams, { replace: true });
          // Update translations and header language display without reload
          changeLanguage(language.code);
          setCurrentLanguage(language);
          // Re-fetch rooms silently with new language (no loader)
          if (hasSearched && selectedDates.checkIn && selectedDates.checkOut) {
            try {
              const checkin = formatDateForAPI(selectedDates.checkIn);
              const checkout = formatDateForAPI(selectedDates.checkOut);
              const accId = accommodationId || urlParams.hotel_id || urlParams.accommodationid || '';
              const params = {
                hotel_id: accId,
                checkin: checkin,
                checkout: checkout,
                adult: guests.adults,
                child: guests.children || 0,
                multilanguageid: language.id || 1,
                currency: currentCurrency?.code || urlParams.currency || '',
                ratecode: urlParams.ratecode || ''
              };
              const apiData = await fetchRoomAvailability(params);
              const transformedRooms = transformApiRoomsToLocalFormat(apiData);
              setAllApiRooms(transformedRooms);
              const initialBatch = transformedRooms.slice(0, 5);
              setApiRooms(initialBatch);
              setDisplayedRoomsCount(5);
            } catch (error) {
              // Silent fail - keep existing data
            }
          }
        }}
        currentCurrency={currentCurrency || (urlParams.currency ? {
          code: urlParams.currency,
          symbol: getCurrencySymbol(urlParams.currency),
          rate: 1,
          name: getCurrencyName(urlParams.currency),
        } : null)}
        onCurrencyChange={async (currency) => {
          setCurrentCurrency(currency);
          localStorage.setItem('selectedCurrency', JSON.stringify(currency));

          // Add currency parameter to URL without page reload
          const newSearchParams = new URLSearchParams(searchParams);
          if (currency?.code) {
            newSearchParams.set('currency', currency.code);
          }
          setSearchParams(newSearchParams, { replace: true });

          // If rooms have been searched, reload data silently (no loader)
          if (hasSearched && selectedDates.checkIn && selectedDates.checkOut) {
            try {
              const checkin = formatDateForAPI(selectedDates.checkIn);
              const checkout = formatDateForAPI(selectedDates.checkOut);
              const accId = accommodationId || urlParams.hotel_id || urlParams.accommodationid || '';

              const params = {
                hotel_id: accId,
                checkin: checkin,
                checkout: checkout,
                adult: guests.adults,
                child: guests.children || 0,
                currency: currency?.code || null,
                ratecode: urlParams.ratecode || ''
              };

              const apiData = await fetchRoomAvailability(params);
              const transformedRooms = transformApiRoomsToLocalFormat(apiData);

              // Store all rooms
              setAllApiRooms(transformedRooms);

              // Display initial batch (first 5 rooms for fast initial load)
              const initialBatch = transformedRooms.slice(0, 5);
              setApiRooms(initialBatch);
              setDisplayedRoomsCount(5);
            } catch (error) {
              const errorMessage = error.message || "Failed to fetch room availability";
              setRoomsError(errorMessage);
            }
          }
        }}
        accommodationGroupDetail={accommodationGroupDetail}
      />

      {showCheckout ? (
        <CheckoutComponent
          selectedRooms={selectedRooms}
          selectedAddOns={selectedAddOns}
          selectedDates={selectedDates}
          guests={guests}
          total={total}
          nights={nights}
          onBackToSummary={handleBackToSummary}
          onCheckoutComplete={handleCheckoutComplete}
        />
      ) : (
        <main className="flex-1 w-full">
          <div className={`w-full relative z-40 px-4 sm:px-6 lg:px-8 mb-6 ${isLandingMode ? 'pt-8' : 'pt-6'}`}>
            {isLandingMode && (
              <div className="max-w-7xl mx-auto text-center mb-6 animate-fade-in">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  {accommodationDetail?.accommodationName || accommodationDetail?.AccommodationName
                    ? `Welcome to ${accommodationDetail.accommodationName || accommodationDetail.AccommodationName}`
                    : 'Your stay starts here'}
                </h2>
                <p className="text-base text-gray-500">
                  Book direct & save more — your perfect room is just a couple of dates away.
                </p>
              </div>
            )}
            <SearchForm
              selectedDates={selectedDates}
              setSelectedDates={setSelectedDates}
              showGuestSelector={showGuestSelector}
              setShowGuestSelector={setShowGuestSelector}
              guests={guests}
              setGuests={setGuests}
              childAges={childAges}
              setChildAges={setChildAges}
              promoCode={promoCode}
              setPromoCode={setPromoCode}
              showAdultsOption={showAdultsOption}
              showChildrenOption={showChildrenOption}
              openCalendarSignal={openCalendarSignal}
              onSearch={handleSearchAvailability}
              isLoading={isLoadingRooms}
              groupId={groupId}
              accommodationId={accommodationId}
              onAccommodationChange={handleAccommodationChange}
              currentLanguage={currentLanguage}
              isAccommodationDropDown={isAccommodationDropDown}
              accommodationDetail={accommodationDetail}
            />
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ">
            {/* Loyalty Banner - Sticky at top while scrolling (Only for logged-out users) */}
            {showLoyaltyBanner && !isLoggedIn && Number(hasLoyalty) === 1 && (
              <div className="sticky top-2 z-30 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 mb-6 fade-in shadow-md">
                <div className="flex items-center justify-between">
                  <div className="flex items-center flex-1">
                    <div className="flex-shrink-0 mr-3">
                      <span className="text-2xl">🔒</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-900 mb-1">
                        {memberDiscountPercent > 0
                          ? t('loyalty.unlockMembersDeal', { percent: memberDiscountPercent })
                          : t('loyalty.membersSaveMore')}
                      </p>
                      <p className="text-xs text-gray-600">
                        {memberDiscountPercent > 0
                          ? t('loyalty.joinFreeSaveLong', { percent: memberDiscountPercent })
                          : t('loyalty.signInToUnlock')}
                      </p>
                      <span className="mt-1.5 inline-flex items-center gap-1.5 bg-emerald-100 border border-emerald-300 text-emerald-800 text-xs font-semibold px-2.5 py-1 rounded-full shadow-sm">
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {t('search.bestPriceTitle')} · {t('search.bestPriceSubtitle')}
                      </span>
                    </div>
                    <button
                      onClick={() => setShowSignInModal(true)}
                      className="ml-4 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
                    >
                      {memberDiscountPercent > 0
                        ? t('loyalty.claimMyOff', { percent: memberDiscountPercent })
                        : t('loyalty.signInToUnlockSavings')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {isLoadingRooms ? (
              /* Loading State - only show on subsequent searches (initial load uses full-page loader) */
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-3">
                  <div className="bg-white rounded-lg shadow-md p-12 text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* Show error or no rooms message without tabs and booking summary */}
                {roomsError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                    <p className="text-red-800 text-sm">{roomsError}</p>
                    <button
                      onClick={handleSearchAvailability}
                      className="mt-2 text-red-600 hover:text-red-800 text-sm underline"
                    >
                      Try again
                    </button>
                  </div>
                )}
                {hasSearched && (!displayRooms || displayRooms.length === 0) && (
                  <div className="bg-white rounded-lg shadow-md p-8 text-center">
                    <div className="mb-4">
                      <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">{t('room.noRoomsAvailable')}</h3>
                    <p className="text-gray-600 mb-2">{t('room.noRoomsAtDate')}</p>
                    <p className="text-gray-600 mb-4">{t('room.changeDateMessage')}</p>
                    <button
                      onClick={() => {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                        setOpenCalendarSignal((n) => n + 1);
                      }}
                      className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium inline-flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      {t('room.searchAgain')}
                    </button>
                  </div>
                )}
                {!hasSearched && !isLandingMode && (!displayRooms || displayRooms.length === 0) && (
                  <div className="bg-white rounded-lg shadow-md p-8 text-center">
                    <p className="text-gray-600">Please select dates and search for room availability</p>
                  </div>
                )}

                {/* Show Tabs and Booking Summary only when rooms are available */}
                {displayRooms && displayRooms.length > 0 && (
                  <>
                    {/* Best-price / book-direct trust bar — only when loyalty banner isn't already showing (avoid double bars) */}
                    {!(showLoyaltyBanner && !isLoggedIn && Number(hasLoyalty) === 1) && (
                    <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 mb-6">
                      <svg className="h-6 w-6 flex-shrink-0 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="leading-tight">
                        <p className="text-sm font-semibold text-emerald-800">{t('search.bestPriceTitle')}</p>
                        <p className="text-xs text-emerald-700">{t('search.bestPriceSubtitle')}</p>
                      </div>
                    </div>
                    )}

                    {/* Tabs Section - Only show if add-ons are available */}
                    {Array.isArray(addOns) && addOns.length > 0 && (
            <Tabs
              tabs={[
                { label: t('room.rooms'), value: "rooms" },
                { label: t('tabs.addOns'), value: "addons" },
              ]}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
            />
                    )}

            {/* Room List and Booking Summary Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left Column - Content Based on Active Tab */}
              <div className="lg:col-span-2">
                        {/* If add-ons available, show tabs-based content, otherwise just show rooms */}
                        {Array.isArray(addOns) && addOns.length > 0 ? (
                          activeTab === "rooms" ? (
                            <>
                  <RoomsSection
                    hotelRooms={displayRooms}
                    selectedRooms={selectedRooms}
                    updateRoomQuantity={updateRoomQuantity}
                    openRoomModal={openRoomModal}
                    guests={guests}
                    childAges={childAges}
                    selectedDates={selectedDates}
                    calculateNights={calculateNights}
                    accommodationDetail={accommodationDetail}
                    currentCurrency={currentCurrency}
                    onOpenLogin={() => setShowSignInModal(true)}
                    isLoggedIn={isLoggedIn}
                    memberDiscountPercent={memberDiscountPercent}
                  />
                              
                              {/* Load More Indicator */}
                              {hasSearched && allApiRooms.length > displayedRoomsCount && (
                                <div className="text-center py-6 mt-4">
                                  {isLoadingMore ? (
                                    <div className="flex flex-col items-center gap-2">
                                      <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                                      <p className="text-sm text-gray-600">Loading more rooms...</p>
                                    </div>
                                  ) : (
                                    <button
                                      onClick={loadMoreRooms}
                                      className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium text-sm"
                                    >
                                      Load More ({allApiRooms.length - displayedRoomsCount} remaining)
                                    </button>
                                  )}
                                </div>
                              )}
                            </>
                ) : (
                  /* Add-ons Section */
                  <>
                    {isLoadingAddOns ? (
                      <div className="text-center py-12">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mb-4"></div>
                        <p className="text-gray-600">Loading add-ons...</p>
                      </div>
                    ) : addOnsError ? (
                      <div className="text-center py-12 text-red-500">
                        <p className="text-lg font-medium">Error loading add-ons</p>
                        <p className="text-sm">{addOnsError}</p>
                      </div>
                    ) : (
                  <AddOnsComponent
                        addOns={addOns || []}
                    selectedAddOns={selectedAddOns}
                    updateAddOnQuantity={updateAddOnQuantity}
                    openCommentModal={openCommentModal}
                    addOnComments={addOnComments}
                    currentCurrency={currentCurrency}
                  />
                )}
                  </>
                          )
                        ) : (
                          <>
                            {/* No add-ons - just show rooms directly */}
                            <RoomsSection
                              hotelRooms={displayRooms}
                              selectedRooms={selectedRooms}
                              updateRoomQuantity={updateRoomQuantity}
                              openRoomModal={openRoomModal}
                              guests={guests}
                              childAges={childAges}
                              selectedDates={selectedDates}
                              calculateNights={calculateNights}
                              accommodationDetail={accommodationDetail}
                              currentCurrency={currentCurrency}
                              onOpenLogin={() => setShowSignInModal(true)}
                              isLoggedIn={isLoggedIn}
                              memberDiscountPercent={memberDiscountPercent}
                            />
                            
                            {/* Load More Indicator */}
                            {hasSearched && allApiRooms.length > displayedRoomsCount && (
                              <div className="text-center py-6 mt-4">
                                {isLoadingMore ? (
                                  <div className="flex flex-col items-center gap-2">
                                    <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                                    <p className="text-sm text-gray-600">Loading more rooms...</p>
              </div>
                                ) : (
                                  <button
                                    onClick={loadMoreRooms}
                                    className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium text-sm"
                                  >
                                    Load More ({allApiRooms.length - displayedRoomsCount} remaining)
                                  </button>
                                )}
                              </div>
                            )}
                          </>
                )}
              </div>

              {/* Right Column - Price Summary */}
              <BookingSummary
                selectedRooms={selectedRooms}
                selectedAddOns={selectedAddOns}
                nights={nights}
                roomSubtotal={roomSubtotal}
                addOnsSubtotal={addOnsSubtotal}
                taxes={taxes}
                addOnComments={addOnComments}
                selectedDates={selectedDates}
                guests={guests}
                calculateNights={calculateNights}
                removeRoom={removeRoom}
                startCheckout={startCheckout}
                total={total}
                accommodationName={accommodationDetail?.accommodationName}
                removeAddOn={removeAddOn}
                currentCurrency={currentCurrency}
              />
            </div>
                  </>
                )}
              </>
            )}
          </div>
        </main>
      )}

      
      <Footer accommodationName={accommodationDetail?.accommodationName || accommodationDetail?.AccommodationName || ''} />

      {/* Room Details Modal */}
      {showModal && selectedRoom && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center p-4 z-[9998]"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden relative flex flex-col md:flex-row shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Left: Image gallery + thumbnails */}
            <div className="md:w-[56%] flex-shrink-0 flex flex-col justify-center gap-3 p-4 md:max-h-[90vh] overflow-hidden">
              <RoomImageSlider
                images={selectedRoom.images || [selectedRoom.image]}
                roomName={selectedRoom.name}
                discount={selectedRoom.discount}
                onImageClick={null}
                showControls={true}
                autoSlide={true}
                size="large"
                className="border-none !rounded-xl w-full"
                externalCurrentIndex={modalCurrentImageIndex}
                onImageChange={setModalCurrentImageIndex}
                roomsAvailable={selectedRoom?.totalroom || null}
              />

              {/* Thumbnail strip */}
              {(selectedRoom.images || []).length > 1 && (
                <div className="flex gap-2 overflow-x-auto flex-shrink-0">
                  {selectedRoom.images.map((img, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setModalCurrentImageIndex(i)}
                      className={`flex-shrink-0 rounded-md overflow-hidden border-2 transition-all ${
                        modalCurrentImageIndex === i ? 'border-emerald-500' : 'border-transparent opacity-70 hover:opacity-100'
                      }`}
                    >
                      <img
                        src={img}
                        alt={`${selectedRoom.name} ${i + 1}`}
                        className="h-14 w-20 object-cover"
                        loading="lazy"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Right: Details */}
            <div className="md:w-[44%] flex flex-col max-h-[90vh]">
              {/* Header: name + close */}
              <div className="flex items-start justify-between gap-3 px-7 pt-6 pb-3">
                <h3 className="text-2xl font-bold leading-tight" style={{color: 'var(--primary-color, #1a1a1a)'}}>
                  {selectedRoom.name}
                </h3>
                <button
                  onClick={closeModal}
                  className="flex-shrink-0 -mr-1 text-gray-400 hover:text-gray-700 transition-colors focus:outline-none"
                  aria-label="Close"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Size + Max guests */}
              {((selectedRoom.size && selectedRoom.size !== 'N/A') || selectedRoom.guests) && (
                <div className="flex items-center flex-wrap gap-x-5 gap-y-2 px-7 pb-4 border-b border-gray-100 text-sm text-gray-700">
                  {selectedRoom.size && selectedRoom.size !== 'N/A' && (
                    <span className="inline-flex items-center gap-1.5">
                      <svg className="h-4 w-4 text-gray-500" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                      </svg>
                      {selectedRoom.size}
                    </span>
                  )}
                  {selectedRoom.guests && (
                    <span className="inline-flex items-center gap-1.5">
                      <span className="text-gray-600">{t('room.upToGuests', { n: selectedRoom.guests })}</span>
                      <span className="inline-flex items-center text-emerald-600">
                        {Array.from({ length: Math.min(Number(selectedRoom.guests) || 0, 6) }).map((_, i) => (
                          <svg key={i} className="h-4 w-4 -ml-0.5 first:ml-0" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                          </svg>
                        ))}
                      </span>
                    </span>
                  )}
                </div>
              )}

              {/* Scrollable body */}
              <div className="px-7 py-5 overflow-y-auto">
                {/* Room Features */}
                {selectedRoom.features?.length > 0 && (
                  <div className="mb-5">
                    <h4 className="font-semibold text-gray-900 mb-3">{t('room.roomFeatures')}</h4>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                      {selectedRoom.features.map((feature, index) => (
                        <span key={index} className="inline-flex items-center gap-1.5 text-sm text-gray-700">
                          <svg className="h-4 w-4 text-emerald-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Amenities as tag pills */}
                {selectedRoom.amenities?.length > 0 && (
                  <div className="mb-5">
                    {!selectedRoom.features?.length && (
                      <h4 className="font-semibold text-gray-900 mb-3">{t('room.amenities')}</h4>
                    )}
                    <div className="flex flex-wrap gap-2">
                      {selectedRoom.amenities.map((amenity, index) => (
                        <span key={index} className="inline-block bg-emerald-50 text-emerald-800 border border-emerald-100 rounded-full px-3 py-1 text-xs font-medium">
                          {amenity}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Grouped facilities (fallback / extra detail) */}
                {selectedRoom.facilityGroups && Object.keys(selectedRoom.facilityGroups).length > 0 && !selectedRoom.amenities?.length && (
                  <div className="mb-5 space-y-3">
                    {Object.entries(selectedRoom.facilityGroups).map(([groupName, facilities]) => (
                      <div key={groupName} className="bg-gray-50 rounded-lg p-3">
                        <h5 className="font-semibold text-gray-800 mb-1.5 text-sm">{groupName}</h5>
                        <div className="flex flex-wrap gap-x-4 gap-y-1">
                          {facilities.map((facility, index) => (
                            <span key={index} className="inline-flex items-center gap-1.5 text-sm text-gray-600">
                              <svg className="h-3 w-3 text-emerald-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              {facility}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Description */}
                {selectedRoom.description && (
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {selectedRoom.description}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {(accommodationGroupDetail?.IsOtpLogin == 1 || accommodationGroupDetail?.isOtpLogin == 1 || accommodationGroupDetail?.accommodationdetailsdb?.[0]?.IsOtpLogin == 1) ? (
        <AuthModal
          show={showSignInModal}
          onClose={() => setShowSignInModal(false)}
          accommodationGroupDetail={accommodationGroupDetail}
          accommodationDetail={accommodationDetail}
          memberPercent={memberDiscountPercent}
        />
      ) : (
        <SignInRegisterModal
          show={showSignInModal}
          onClose={() => setShowSignInModal(false)}
          signInTab={signInTab}
          setSignInTab={setSignInTab}
          accommodationGroupDetail={accommodationGroupDetail}
          accommodationDetail={accommodationDetail}
        />
      )}


      {/* Comment Modal */}
      {showCommentModal && selectedAddOnForComment && (
        <CommentModal
          closeCommentModal={closeCommentModal}
          selectedAddOnForComment={selectedAddOnForComment}
          saveComment={saveComment}
          addOnComments={addOnComments}
        />
      )}

      
    </div>
  );
};

export default DesktopComponent;
